import RecordRTC, { type State } from "recordrtc";
import { v4 as uuidv4 } from "uuid";
import { type ReturnData } from "src/types";
import { logError, logInfo, logWarn } from "src/services/logger";

export let Recorder: {
  startRecording: (config?: RecorderConfigType) => Promise<ReturnData>;
  stopRecording: (getBlob?: boolean) => Promise<ReturnData>;
  pauseRecording: Function;
  resumeRecording: Function;
  addOnDataListner: (hook: Function) => string;
  removeDataListner: (hookRef: string) => void;
  setVideoStreams: (streams: Array<MediaStream>) => void;
  addMediaStreams: (streams: Array<MediaStream>) => void;
  getStatus: () => Promise<State> | undefined;
};

type RecorderConfigType = {
  videoBitsPerSecond?: number;
  video?: {
    width: number;
    height: number;
  };
};

type RecordingVideoStream = MediaStream & {
  width?: number;
  height?: number;
  left?: number;
  top?: number;
};

type HookType = {
  ref: string;
  hook: Function;
};

export const createRecorderInstance = () => {
  logInfo("Recorder: iniciado");

  let _Recorder: RecordRTC.RecordRTCPromisesHandler | undefined;

  // Main state
  const state: {
    isRecording: boolean;
    mediaStreams: Array<MediaStream>;
  } = {
    isRecording: false,
    mediaStreams: [],
  };

  let recordingVideoConfig: RecorderConfigType["video"];

  const applyVideoLayout = (streams: Array<MediaStream>) => {
    if (!recordingVideoConfig) return streams;

    let videoIndex = 0;

    streams.forEach((stream) => {
      if (!stream.getVideoTracks().length) return;

      Object.assign(stream as RecordingVideoStream, {
        width: recordingVideoConfig.width,
        height: recordingVideoConfig.height,
        left: videoIndex * recordingVideoConfig.width,
        top: 0,
      });

      videoIndex += 1;
    });

    return streams;
  };

  const initRecorder = (config?: RecorderConfigType) => {
    if (!state.mediaStreams.length) return { success: false, message: "Nenhuma media stream encontrada." };

    recordingVideoConfig = config?.video;
    const mediaStreams = applyVideoLayout(state.mediaStreams);

    // @ts-ignore: Unreachable code error
    _Recorder = new RecordRTC.RecordRTCPromisesHandler(mediaStreams, {
      type: "video",
      mimeType: "video/webm;codecs=vp8",
      recorderType: RecordRTC.MultiStreamRecorder,
      videoBitsPerSecond: 128000, // NORMAL
      disableLogs: false,
      timeSlice: 5000,
      ondataavailable: onDataAvailable,

      ...config,
    });

    return { success: true, instance: _Recorder };
  };

  // Hooks
  const activeHooks: Array<HookType> = [];
  async function onDataAvailable(blob: Blob) {
    if (state.isRecording) {
      activeHooks.forEach((h) => {
        h.hook(blob);
      });
    } else {
      logWarn("Recorder: onDataAvailable sem gravação em andamento");
    }
  }

  const addOnDataListner = (hook: Function) => {
    const newHook = {
      ref: uuidv4(),
      hook,
    };

    activeHooks.push(newHook);

    return newHook.ref;
  };

  const removeDataListner = (ref: string) => {
    const foundHookIndex = activeHooks.findIndex((h) => h.ref === ref);

    return foundHookIndex > -1 ? activeHooks.splice(foundHookIndex, 1).length > 0 : false;
  };

  // Methods
  const startRecording = async (config?: RecorderConfigType) => {
    // Create
    if (!_Recorder) {
      const { success, message } = await initRecorder(config);

      if (!success) return { success, message };

      _Recorder!.startRecording();

      // state.status = "RECORDING";
    } else {
      // Reset and Record
      _Recorder.reset();
      _Recorder.startRecording();

      // Get recorder
      const internalRecorder = await _Recorder.getInternalRecorder();

      // Reset video tracks & add audio tracks
      if (internalRecorder instanceof RecordRTC.MultiStreamRecorder) {
        internalRecorder.resetVideoStreams(applyVideoLayout(state.mediaStreams));

        const audioTracks: Array<MediaStreamTrack> = [];

        state.mediaStreams.forEach((s) => s.getAudioTracks().forEach((t) => audioTracks.push(t)));

        const audioStream = new MediaStream(audioTracks);
        internalRecorder.addStreams([audioStream]);
      }
    }

    // Init recording
    state.isRecording = true;

    return {
      success: true,
    };
  };

  const resumeRecording = () => {
    if (_Recorder) {
      state.isRecording = true;
      _Recorder.resumeRecording();
    }
  };

  const pauseRecording = () => {
    if (_Recorder) {
      state.isRecording = false;
      _Recorder.pauseRecording();
    }
  };

  const stopRecording = async (getBlob = false) => {
    if (_Recorder) {
      try {
        let data: any;

        const recorderTrueState: RecordRTC.State = await _Recorder.getState();

        // Mark recording stoped
        if (recorderTrueState === "recording") await _Recorder.stopRecording();
        state.isRecording = false;

        if (getBlob) {
          data = await _Recorder.getBlob();
        }

        // Reset instance
        await _Recorder.reset();
        _Recorder = undefined;
        state.mediaStreams = [];
        recordingVideoConfig = undefined;

        return {
          success: true,
          data,
        };
      } catch (ex: any) {
        logError("Recorder: falha ao finalizar gravação", ex);

        return {
          success: false,
          message: "Falha ao finalizar gravação: " + (ex?.message ?? ""),
        };
      }
    }

    return {
      success: false,
      message: "Erro ao parar gravação: Gravador não inicializado",
    };
  };

  const setVideoStreams = async (streams: Array<MediaStream>) => {
    if (_Recorder) {
      const internalRecorder = await _Recorder.getInternalRecorder();

      if (internalRecorder instanceof RecordRTC.MultiStreamRecorder) {
        internalRecorder.resetVideoStreams(applyVideoLayout(streams));
      }

      return { success: true };
    }

    return { success: false, message: "Falha ao setar as streams" };
  };

  const addMediaStreams = async (streams: Array<MediaStream>) => {
    if (!streams) return;

    if (_Recorder) {
      const internalRecorder = await _Recorder.getInternalRecorder();
      if (internalRecorder instanceof RecordRTC.MultiStreamRecorder) {
        internalRecorder.addStreams(streams);
      }
    } else state.mediaStreams = state.mediaStreams.concat(streams);
  };

  return (Recorder = {
    startRecording,
    stopRecording,
    pauseRecording,
    addOnDataListner,
    removeDataListner,
    resumeRecording,
    addMediaStreams,
    setVideoStreams,

    getStatus: () => _Recorder?.getState(),
  });
};

export default createRecorderInstance;
